import React from "react";
import { styled } from "styled-components";
import { useFormContext } from "react-hook-form";

import ColorField from "../ColorField";
import { WebAppTheme } from "../../types/web";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Separator = styled.hr`
  width: 100%;
`;

export default function PrimaryWebThemeForm() {
  const { watch } = useFormContext<WebAppTheme>();
  const [
    heading,
    body,
    warning,
    primaryButtonHover,
    secondaryButtonHover,
    tertiaryButtonHover,
    primarySurfaces,
    secondarySurfaces,
    skeletonSurfaces,
    inputSurfaces,
    authPageStatusSurfaces,
    highlights,
    spinners,
    navBarText,
    navBarButtonBackground,
    navBarButtonContentDefault,
    navBarButtonContentAccent,
    orderStatusText,
    calendarText,
  ] = watch([
    "text.heading",
    "text.body",
    "text.warning",
    "button.primary.background.hover",
    "button.secondary.background.hover",
    "button.tertiary.background.hover",
    "surfaces.primary",
    "surfaces.secondary",
    "surfaces.components.skeleton",
    "surfaces.components.input",
    "surfaces.components.authPageStatus",
    "highlights",
    "components.spinners",
    "components.navBar.text",
    "components.navBar.button.background",
    "components.navBar.button.content.default",
    "components.navBar.button.content.accent",
    "components.tags.orderStatus.text",
    "components.calendar.text",
  ]);

  return (
    <Container>
      <h2>Text</h2>
      <h4>Heading</h4>
      {heading.map((heading, index) => (
        <ColorField label={`Heading ${index}`} name={`text.heading.${index}`} />
      ))}
      <h4>Body</h4>
      {body.map((heading, index) => (
        <ColorField label={`Body ${index}`} name={`text.body.${index}`} />
      ))}
      <h4>Danger</h4>
      <ColorField label={"Danger"} name={`text.danger`} />
      <h4>Success</h4>
      <ColorField label={"Success"} name={`text.success`} />
      <h4>Warning</h4>
      {warning.map((heading, index) => (
        <ColorField label={`Warning ${index}`} name={`text.warning.${index}`} />
      ))}
      <Separator />
      <h2>Button</h2>
      <h4>Primary</h4>
      <ColorField
        label={"Background Default"}
        name={"button.primary.background.default"}
      />
      <ColorField label={"Content"} name={"button.primary.content"} />
      {primaryButtonHover.map((hover, index) => (
        <ColorField
          label={`Background Hover ${index}`}
          name={`button.primary.background.hover.${index}`}
        />
      ))}
      <h4>Secondary</h4>
      <ColorField
        label={"Background Default"}
        name={"button.secondary.background.default"}
      />
      <ColorField label={"Content"} name={"button.secondary.content"} />
      {secondaryButtonHover.map((hover, index) => (
        <ColorField
          label={`Background Hover ${index}`}
          name={`button.secondary.background.hover.${index}`}
        />
      ))}
      <h4>Tertiary</h4>
      <ColorField
        label={"Background Default"}
        name={"button.tertiary.background.default"}
      />
      <ColorField label={"Content"} name={"button.tertiary.content"} />
      {tertiaryButtonHover.map((hover, index) => (
        <ColorField
          label={`Background Hover ${index}`}
          name={`button.tertiary.background.hover.${index}`}
        />
      ))}
      <Separator />
      <h2>Surfaces</h2>
      <h4>Body</h4>
      <ColorField label={"Body"} name={"surfaces.body"} />
      <h4>Primary</h4>
      {primarySurfaces.map(
        (surface, index) =>
          surface && (
            <ColorField
              label={`Primary Surface ${index}`}
              name={`surfaces.primary.${index}`}
            />
          )
      )}
      <h4>Secondary</h4>
      {secondarySurfaces.map(
        (surface, index) =>
          surface && (
            <ColorField
              label={`Secondary Surface ${index}`}
              name={`surfaces.secondary.${index}`}
            />
          )
      )}
      <h3>Components</h3>
      <h4>Skeleton</h4>
      {skeletonSurfaces.map(
        (surface, index) =>
          surface && (
            <ColorField
              label={`Skeleton Surface ${index}`}
              name={`surfaces.components.skeleton.${index}`}
            />
          )
      )}
      <h4>Input</h4>
      {inputSurfaces.map(
        (surface, index) =>
          surface && (
            <ColorField
              label={`Input Surface ${index}`}
              name={`surfaces.components.input.${index}`}
            />
          )
      )}
      <h4>Auth Page Status</h4>
      {authPageStatusSurfaces.map(
        (surface, index) =>
          surface && (
            <ColorField
              label={`Auth Page Status Surface ${index}`}
              name={`surfaces.components.authPageStatus.${index}`}
            />
          )
      )}
      <Separator />
      <h2>Borders</h2>
      <ColorField label={"Primary"} name={"borders.primary"} />
      <ColorField label={"Secondary"} name={"borders.secondary"} />
      <ColorField label={"Tertiary"} name={"borders.tertiary"} />
      <Separator />
      <h2>Highlights</h2>
      {highlights.map((highlight, index) => (
        <ColorField label={`Highlight ${index}`} name={`highlights.${index}`} />
      ))}
      <Separator />
      <h2>Components</h2>
      <h3>Alert</h3>
      <h4>Success</h4>
      <ColorField
        label={"Background"}
        name={"components.alert.success.background"}
      />
      <ColorField label={"Text"} name={"components.alert.success.text"} />
      <ColorField label={"Border"} name={"components.alert.success.border"} />
      <h4>Danger</h4>
      <ColorField
        label={"Background"}
        name={"components.alert.danger.background"}
      />
      <ColorField label={"Text"} name={"components.alert.danger.text"} />
      <ColorField label={"Border"} name={"components.alert.danger.border"} />
      <h3>Toast</h3>
      <ColorField label={"Success"} name={"components.toast.success"} />
      <ColorField label={"Error"} name={"components.toast.error"} />
      <ColorField label={"Info"} name={"components.toast.info"} />
      <h3>Spinners</h3>
      {spinners.map((spinner, index) => (
        <ColorField
          label={`Spinner ${index}`}
          name={`components.spinners.${index}`}
        />
      ))}
      <h3>Navigation Bar</h3>
      <ColorField label={"Background"} name={"components.navBar.background"} />
      <h4>Text</h4>
      {navBarText.map((text, index) => (
        <ColorField
          label={`Text ${index}`}
          name={`components.navBar.text.${index}`}
        />
      ))}
      <h4>Button</h4>
      {navBarButtonBackground.map((background, index) => (
        <ColorField
          label={`Background ${index}`}
          name={`components.navBar.button.background.${index}`}
        />
      ))}
      <h4>Content</h4>
      {navBarButtonContentDefault.map((content, index) => (
        <ColorField
          label={`Default ${index}`}
          name={`components.navBar.button.content.default.${index}`}
        />
      ))}
      {navBarButtonContentAccent.map((content, index) => (
        <ColorField
          label={`Accent ${index}`}
          name={`components.navBar.button.content.accent.${index}`}
        />
      ))}
      <h3>Tags</h3>
      <h4>Order Status</h4>
      {orderStatusText.map((text, index) => (
        <>
          <ColorField
            label={`Background ${index}`}
            name={`components.tags.orderStatus.background.${index}`}
          />
          <ColorField
            label={`Text ${index}`}
            name={`components.tags.orderStatus.text.${index}`}
          />
        </>
      ))}
      <h4>Preparation Time</h4>
      <ColorField
        label={"Background"}
        name={`components.tags.preparationTime.background`}
      />
      <ColorField
        label={"Text"}
        name={`components.tags.preparationTime.text`}
      />
      <h3>Calendar</h3>
      {calendarText.map((text, index) => (
        <>
          <ColorField
            label={`Calendar Text ${index}`}
            name={`components.calendar.text.${index}`}
          />
          <ColorField
            label={`Calendar Background ${index}`}
            name={`components.calendar.background.${index}`}
          />
        </>
      ))}
    </Container>
  );
}
